import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import BottomTabNavigation from '../../components/ui/BottomTabNavigation';
import NavigationBreadcrumbs from '../../components/ui/NavigationBreadcrumbs';
import TemplateCard from './components/TemplateCard';
import TemplateEditor from './components/TemplateEditor';
import CategoryFilter from './components/CategoryFilter';
import TemplateSearchBar from './components/TemplateSearchBar';
import BulkActions from './components/BulkActions';
import TemplateStats from './components/TemplateStats';

const MessageTemplates = () => {
  const navigate = useNavigate();
  const [templates, setTemplates] = useState([]);
  const [filteredTemplates, setFilteredTemplates] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [sortBy, setSortBy] = useState('name');
  const [selectedTemplates, setSelectedTemplates] = useState([]);
  const [showEditor, setShowEditor] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [showStats, setShowStats] = useState(false);

  // Mock template data
  const mockTemplates = [
    {
      id: '1',
      name: 'Happy Birthday Wishes',
      category: 'birthday',
      content: `Happy Birthday, {Name}! 🎉\n\nWishing you a fantastic day filled with joy and celebration. Hope your {Birthday} is as amazing as you are!\n\nBest wishes,\nYour friend`,
      variables: ['Name', 'Birthday'],
      tags: ['birthday', 'celebration', 'personal'],
      usageCount: 45,
      lastModified: '2025-01-10T10:30:00Z'
    },
    {
      id: '2',
      name: 'Professional Follow-up',
      category: 'follow-up',
      content: `Hi {Name},\n\nI hope this message finds you well. It's been {LastContact} since we last connected, and I wanted to reach out to see how things are going at {Company}.\n\nWould love to catch up soon!\n\nBest regards`,
      variables: ['Name', 'LastContact', 'Company'],
      tags: ['professional', 'networking', 'business'],
      usageCount: 32,
      lastModified: '2025-01-08T14:15:00Z'
    },
    {
      id: '3',name: 'Holiday Greetings',category: 'holiday',content: `Season's Greetings, {Name}!\n\nWishing you and your loved ones a wonderful holiday season filled with peace, joy, and happiness.\n\nMay the new year bring you prosperity and success!\n\nWarm regards`,
      variables: ['Name'],
      tags: ['holiday', 'seasonal', 'greetings'],
      usageCount: 28,
      lastModified: '2025-01-05T09:45:00Z'
    },
    {
      id: '4',
      name: 'Quick Check-in',
      category: 'follow-up',
      content: `Hey {Name}!\n\nJust wanted to drop a quick note to see how you're doing. It's been a while since we last talked!\n\nHope everything is going well with you.\n\nTalk soon!`,
      variables: ['Name'],
      tags: ['casual', 'check-in', 'friendly'],
      usageCount: 19,
      lastModified: '2025-01-03T16:20:00Z'
    },
    {
      id: '5',
      name: 'Thank You Note',
      category: 'custom',
      content: `Dear {Name},\n\nI wanted to take a moment to thank you for your kindness and support. Your help with {LastContact} meant so much to me.\n\nI'm grateful to have you in my life.\n\nWith appreciation`,
      variables: ['Name', 'LastContact'],
      tags: ['gratitude', 'appreciation', 'personal'],
      usageCount: 15,
      lastModified: '2025-01-01T11:30:00Z'
    },
    {
      id: '6',name: 'Milestone Celebration',category: 'custom',
      content: `Congratulations, {Name}! 🎊\n\nI heard about your recent achievement and wanted to celebrate with you. Your hard work and dedication at {Company} have truly paid off.\n\nYou deserve all the success coming your way!\n\nCheers to you!`,
      variables: ['Name', 'Company'],
      tags: ['congratulations', 'milestone', 'achievement'],
      usageCount: 12,
      lastModified: '2024-12-28T13:45:00Z'
    }
  ];

  useEffect(() => {
    setTemplates(mockTemplates);
  }, []);

  useEffect(() => {
    let filtered = [...templates];

    // Filter by category
    if (selectedCategory !== 'all') {
      filtered = filtered?.filter(template => template?.category === selectedCategory);
    }

    // Filter by search query
    if (searchQuery?.trim()) {
      const query = searchQuery?.toLowerCase();
      filtered = filtered?.filter(template =>
        template?.name?.toLowerCase()?.includes(query) ||
        template?.content?.toLowerCase()?.includes(query) ||
        template?.category?.toLowerCase()?.includes(query) ||
        template?.tags?.some(tag => tag?.toLowerCase()?.includes(query))
      );
    }

    // Sort templates
    filtered?.sort((a, b) => {
      switch (sortBy) {
        case 'name':
          return a?.name?.localeCompare(b?.name);
        case 'name_desc':
          return b?.name?.localeCompare(a?.name);
        case 'recent':
          return new Date(b.lastModified) - new Date(a.lastModified);
        case 'usage':
          return (b?.usageCount || 0) - (a?.usageCount || 0);
        case 'category':
          return a?.category?.localeCompare(b?.category);
        default:
          return 0;
      }
    });

    setFilteredTemplates(filtered);
  }, [templates, selectedCategory, searchQuery, sortBy]);

  const getTemplateCounts = () => {
    const counts = { all: templates?.length };
    templates?.forEach(template => {
      counts[template.category] = (counts?.[template?.category] || 0) + 1;
    });
    return counts;
  };

  const handleCreateTemplate = () => {
    setEditingTemplate(null);
    setShowEditor(true);
  };

  const handleEditTemplate = (template) => {
    setEditingTemplate(template);
    setShowEditor(true);
  };

  const handleSaveTemplate = (templateData) => {
    if (editingTemplate) {
      setTemplates(prev => prev?.map(t => t?.id === templateData?.id ? templateData : t));
    } else {
      setTemplates(prev => [...prev, templateData]);
    }
  };

  const handleDuplicateTemplate = (template) => {
    const duplicated = {
      ...template,
      id: Date.now()?.toString(),
      name: `${template?.name} (Copy)`,
      usageCount: 0,
      lastModified: new Date()?.toISOString()
    };
    setTemplates(prev => [...prev, duplicated]);
  };

  const handleDeleteTemplate = (template) => {
    if (window.confirm(`Are you sure you want to delete "${template?.name}"?`)) {
      setTemplates(prev => prev?.filter(t => t?.id !== template?.id));
      setSelectedTemplates(prev => prev?.filter(id => id !== template?.id));
    }
  };

  const handleUseTemplate = (template) => {
    navigate('/random-contact', { state: { selectedTemplate: template } });
  };

  const handleSelectTemplate = (templateId, isSelected) => {
    if (isSelected) {
      setSelectedTemplates(prev => [...prev, templateId]);
    } else {
      setSelectedTemplates(prev => prev?.filter(id => id !== templateId));
    }
  };

  const handleSelectAll = () => {
    setSelectedTemplates(filteredTemplates?.map(t => t?.id));
  };

  const handleDeselectAll = () => {
    setSelectedTemplates([]);
  };

  const handleBulkDelete = (templateIds) => {
    if (window.confirm(`Are you sure you want to delete ${templateIds?.length} templates?`)) {
      setTemplates(prev => prev?.filter(t => !templateIds?.includes(t?.id)));
      setSelectedTemplates([]);
    }
  };

  const handleBulkDuplicate = (templateIds) => {
    const templatesToDuplicate = templates?.filter(t => templateIds?.includes(t?.id));
    const duplicated = templatesToDuplicate?.map(template => ({
      ...template,
      id: `${template?.id}_copy_${Date.now()}`,
      name: `${template?.name} (Copy)`,
      usageCount: 0,
      lastModified: new Date()?.toISOString()
    }));
    setTemplates(prev => [...prev, ...duplicated]);
    setSelectedTemplates([]);
  };

  const handleBulkExport = (templateIds) => {
    const templatesToExport = templates?.filter(t => templateIds?.includes(t?.id));
    const dataStr = JSON.stringify(templatesToExport, null, 2);
    const dataBlob = new Blob([dataStr], { type: 'application/json' });
    const url = URL.createObjectURL(dataBlob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `message-templates-${new Date()?.toISOString()?.split('T')?.[0]}.json`;
    link?.click();
    URL.revokeObjectURL(url);
    setSelectedTemplates([]);
  };

  return (
    <div className="min-h-screen bg-background">
      {/* Navigation */}
      <BottomTabNavigation />
      <NavigationBreadcrumbs />
      {/* Main Content */}
      <div className="lg:ml-60 pb-20 lg:pb-0">
        <div className="max-w-7xl mx-auto p-4 lg:p-6">
          {/* Header */}
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between mb-6">
            <div>
              <h1 className="text-2xl lg:text-3xl font-bold text-text-primary mb-2">
                Message Templates
              </h1>
              <p className="text-text-secondary">
                Create and manage reusable message templates for all your communication needs
              </p>
            </div>
            <div className="flex items-center space-x-3 mt-4 lg:mt-0">
              <Button
                variant="outline"
                onClick={() => setShowStats(!showStats)}
                iconName="BarChart3"
                iconPosition="left"
                className="hidden lg:flex"
              >
                {showStats ? 'Hide Stats' : 'View Stats'}
              </Button>
              <Button
                variant="default"
                onClick={handleCreateTemplate}
                iconName="Plus"
                iconPosition="left"
              >
                Create Template
              </Button>
            </div>
          </div>

          {/* Stats Panel */}
          {showStats && (
            <div className="mb-6">
              <TemplateStats templates={templates} />
            </div>
          )}

          {/* Search and Filters */}
          <div className="mb-6">
            <TemplateSearchBar
              searchQuery={searchQuery}
              onSearchChange={setSearchQuery}
              sortBy={sortBy}
              onSortChange={setSortBy}
            />
          </div>

          {/* Bulk Actions */}
          {selectedTemplates?.length > 0 && (
            <div className="mb-6">
              <BulkActions
                selectedTemplates={selectedTemplates}
                totalTemplates={filteredTemplates?.length}
                onSelectAll={handleSelectAll}
                onDeselectAll={handleDeselectAll}
                onBulkDelete={handleBulkDelete}
                onBulkDuplicate={handleBulkDuplicate}
                onBulkExport={handleBulkExport}
              />
            </div>
          )}

          {/* Main Layout */}
          <div className="flex flex-col lg:flex-row gap-6">
            {/* Sidebar - Categories */}
            <div className="lg:w-64 flex-shrink-0">
              <CategoryFilter
                selectedCategory={selectedCategory}
                onCategoryChange={setSelectedCategory}
                templateCounts={getTemplateCounts()}
              />
            </div>

            {/* Templates Grid */}
            <div className="flex-1">
              {filteredTemplates?.length > 0 ? (
                <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
                  {filteredTemplates?.map((template) => (
                    <div key={template?.id} className="relative">
                      {/* Selection Checkbox */}
                      <div className="absolute top-2 left-2 z-10">
                        <input
                          type="checkbox"
                          checked={selectedTemplates?.includes(template?.id)}
                          onChange={(e) => handleSelectTemplate(template?.id, e?.target?.checked)}
                          className="w-4 h-4 text-primary bg-surface border-border rounded focus:ring-primary focus:ring-2"
                        />
                      </div>
                      
                      <TemplateCard
                        template={template}
                        onEdit={handleEditTemplate}
                        onDuplicate={handleDuplicateTemplate}
                        onDelete={handleDeleteTemplate}
                        onUse={handleUseTemplate}
                        className="pt-8"
                      />
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-12">
                  <div className="w-24 h-24 bg-muted rounded-full flex items-center justify-center mx-auto mb-4">
                    <Icon name="FileText" size={32} className="text-text-secondary" />
                  </div>
                  <h3 className="text-lg font-semibold text-text-primary mb-2">
                    {searchQuery ? 'No templates found' : 'No templates yet'}
                  </h3>
                  <p className="text-text-secondary mb-6 max-w-md mx-auto">
                    {searchQuery 
                      ? `No templates match your search for "${searchQuery}". Try adjusting your search terms.`
                      : 'Get started by creating your first message template. Templates help you send consistent, personalized messages quickly.'
                    }
                  </p>
                  {!searchQuery && (
                    <Button
                      variant="default"
                      onClick={handleCreateTemplate}
                      iconName="Plus"
                      iconPosition="left"
                    >
                      Create Your First Template
                    </Button>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
      {/* Template Editor Modal */}
      <TemplateEditor
        template={editingTemplate}
        isOpen={showEditor}
        onClose={() => setShowEditor(false)}
        onSave={handleSaveTemplate}
      />
      {/* Floating Action Button - Mobile */}
      <div className="lg:hidden fixed bottom-20 right-4 z-50">
        <Button
          variant="default"
          size="icon"
          onClick={handleCreateTemplate}
          className="w-14 h-14 rounded-full shadow-modal hover-scale"
        >
          <Icon name="Plus" size={24} />
        </Button>
      </div>
    </div>
  );
};

export default MessageTemplates;